import { Box, CircularProgress, useTheme } from '@mui/material';
import { FC, SyntheticEvent, useRef } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { List, RowComponentProps } from 'react-window';
import { useInfiniteLoader } from 'react-window-infinite-loader';

import { UserMin, UserMinListParams } from '@apiContract';

import { sxList } from '../../styles';
import { UserMap } from '../../types';
import UserSearchField from '../UserSearchField';
import AllUsersItem from './AllUsersItem';
import SelectedUsersItem from './SelectedUsersItem';
import { useUserList } from './hook';

type ListProps = {
  deletedUsers: UserMap;
  selectedUsers: UserMap;
  onItemClick: (user: UserMin) => void;
  filters?: UserMinListParams;
  isSelected: boolean;
  model: string;
  skipRequest?: boolean;
  noItemsSetMessage?: string;
  filterOwner?: boolean;
};

const sxCenterBox = { display: 'flex', justifyContent: 'center', alignItems: 'flex-start', flex: 1 };

const errorComponent = (search: string, model: string, noItemsSetMessage?: string) => (
  <Box sx={sxCenterBox}>{search ? 'No results found' : noItemsSetMessage || `No ${model.toLowerCase()} set`}</Box>
);

const getItemSize = (newItemsCount: number) => (index: number) => {
  if (newItemsCount) {
    if (newItemsCount === 1 && index === 0) return 95;
    if (index === 0) return 78;
    if (index === newItemsCount - 1) return 77;
  }
  return 60;
};

const UserItem = ({
  style,
  index,
  data,
}: RowComponentProps<{
  data: {
    newItemsCount: number;
    deletedUsers: UserMap;
    selectedUsers: UserMap;
    items: UserMin[];
    isAllUsersTab: boolean;
  };
}>) => {
  const user = data.items[index];
  const userId = user.id;

  if (!userId || !user) {
    return <></>;
  }

  return data.isAllUsersTab ? (
    <AllUsersItem index={index} style={style} selected={!!data.selectedUsers[userId]} user={user} />
  ) : (
    <SelectedUsersItem
      isNewBadge={!!data.newItemsCount && index === 0}
      index={index}
      divider={!!data.newItemsCount && index === data.newItemsCount - 1}
      style={style}
      selected={!data.deletedUsers[userId] || !!data.selectedUsers[userId]}
      user={user}
    />
  );
};

const UsersList: FC<ListProps> = ({
  deletedUsers,
  selectedUsers,
  onItemClick,
  filters = {},
  isSelected = false,
  model,
  skipRequest,
  noItemsSetMessage,
  filterOwner = false,
}) => {
  const listRef = useRef(null);
  const sizeRef = useRef<number>(0);
  const { items, isLoading, loadNextPage, isItemLoaded, onSearch, search, searchDebounce } = useUserList({
    filters,
    skipRequest,
    filterOwner,
  });
  const theme = useTheme();

  const selectedArray = Object.values(selectedUsers);
  const filteredSelected = isSelected
    ? selectedArray.filter(({ displayName }) => displayName.toLowerCase().includes(searchDebounce.toLowerCase()))
    : selectedArray;

  const itemsToShow = isSelected ? [...filteredSelected, ...items] : items;

  const itemCount = itemsToShow.length;

  const isDynamicHeight = isSelected && selectedArray.length;

  const itemSize = isDynamicHeight ? getItemSize(filteredSelected.length) : 60;

  if (isDynamicHeight && filteredSelected.length !== sizeRef.current) {
    sizeRef.current = filteredSelected.length;
  }

  const onAutosizerClick = (event: SyntheticEvent<HTMLElement>) => {
    let target = event.target as HTMLElement;

    while (target && (!target.dataset.userindex || target.dataset.stop === 'stop')) {
      target = target.parentElement as HTMLElement;

      if (!target || target === event.currentTarget) {
        return;
      }
    }

    const index = target?.dataset.userindex;
    if (!index) return;
    onItemClick(itemsToShow[+index]);
  };

  const onRowsRendered = useInfiniteLoader({
    isRowLoaded: isItemLoaded,
    loadMoreRows: loadNextPage,
    rowCount: itemCount + 1,
    threshold: 50,
  });

  return (
    <>
      <UserSearchField search={search} handleSearchChange={onSearch} />

      <Box sx={{ ...sxList(theme) }}>
        {isLoading && (
          <Box sx={sxCenterBox}>
            <CircularProgress />
          </Box>
        )}

        {!isLoading && !itemsToShow.length && errorComponent(search, model, noItemsSetMessage)}

        {!isLoading && !!itemsToShow.length && (
          <AutoSizer data-stop="stop" onClick={onAutosizerClick}>
            {({ height, width }) => {
              return (
                <List
                  style={{ height, width, maxHeight: 'inherit' }}
                  onRowsRendered={onRowsRendered}
                  rowCount={itemCount}
                  rowHeight={itemSize}
                  listRef={listRef}
                  rowProps={{
                    data: {
                      newItemsCount: filteredSelected.length,
                      deletedUsers,
                      selectedUsers,
                      items: itemsToShow,
                      isAllUsersTab: !isSelected,
                    },
                  }}
                  className="customScrollBar"
                  rowComponent={UserItem}
                />
              );
            }}
          </AutoSizer>
        )}
      </Box>
    </>
  );
};

export default UsersList;
